/**
 * The demo AI plan before signing up (backend api/demo.py, T-130; web since T-155), for the
 * first-goal flow (T-157). No account: every call carries this browser's demo device id and says
 * it is the web. Over the daily caps the server answers 429 DEMO_BUSY and the flow offers a
 * ready sample plan instead.
 */
import axios, { AxiosInstance } from 'axios';
import { API_CONFIG } from '../config/api.config';
import { languageHeader } from '../services/apiInterceptors';
import {
  AIGenerationJobStatusResponse,
  AIGenerationJobSubmitResponse,
  AIGoalPlan,
  PlanPreferences,
  PlanQuestion,
} from '../services/aiService';
import { isAxiosError } from '../types/error';
import { demoDeviceId } from './onboardingStore';

/** The goal text the demo accepts (backend DEMO_MESSAGE_MAX_LENGTH). */
export const DEMO_GOAL_MAX_LENGTH = 200;

/** Ready plans for the example goals, the same file the app bundles (riselio_app
 * assets/onboarding/sample_plans.json, T-145), in public/onboarding/. */
export const SAMPLE_PLANS_URL = `${process.env.PUBLIC_URL || ''}/onboarding/sample_plans.json`;

let client: AxiosInstance | null = null;

const demoApi = (): AxiosInstance => {
  if (client) return client;
  client = axios.create({ baseURL: API_CONFIG.BASE_URL, timeout: API_CONFIG.TIMEOUTS.DEFAULT, headers: API_CONFIG.HEADERS });
  client.interceptors.request.use((config) => {
    Object.assign(config.headers, languageHeader(), {
      'X-Demo-Device': demoDeviceId(),
      'X-Demo-Client': 'web',
    });
    return config;
  });
  return client;
};

const withPreferences = (preferences?: PlanPreferences | null) =>
  preferences && Object.keys(preferences).length > 0 ? { preferences } : {};

/** Up to 3 questions about the goal; an empty list when the AI has nothing to ask. */
export const demoPlanQuestions = async (message: string, preferences?: PlanPreferences | null): Promise<PlanQuestion[]> => {
  const response = await demoApi().post('/demo/plan-questions', { message, ...withPreferences(preferences) });
  return (response.data.questions || []).slice(0, 3);
};

export const submitDemoPlan = async (message: string, preferences?: PlanPreferences | null): Promise<AIGenerationJobSubmitResponse> => {
  const response = await demoApi().post('/demo/plan-jobs', { message, ...withPreferences(preferences) });
  return response.data;
};

export type DemoJobStatus = AIGenerationJobStatusResponse & { revisions_left?: number };

export const demoPlanStatus = async (jobId: string): Promise<DemoJobStatus> => {
  const response = await demoApi().get(`/demo/plan-jobs/${jobId}`);
  return response.data;
};

/** One change on the finished plan; returns the whole new plan and the changes left. */
export const reviseDemoPlan = async (
  jobId: string,
  plan: AIGoalPlan,
  feedback: string,
): Promise<{ plan: AIGoalPlan; revisionsLeft: number }> => {
  const response = await demoApi().post(`/demo/plan-jobs/${jobId}/revise`, { plan, feedback });
  const { revisions_left: revisionsLeft, success, ...rest } = response.data;
  return { plan: { ...plan, ...rest, success: true } as AIGoalPlan, revisionsLeft: revisionsLeft ?? 0 };
};

/** The sample plans in a language (English when it has none). */
export const loadSamplePlans = async (language: string): Promise<AIGoalPlan[]> => {
  const response = await fetch(SAMPLE_PLANS_URL);
  if (!response.ok) throw new Error(`sample plans: ${response.status}`);
  const all = (await response.json()) as Record<string, AIGoalPlan[]>;
  // The samples have no description or end date; the plan type wants strings
  return (all[language] || all.en || []).map((plan) => ({
    ...plan,
    description: plan.description || '',
    estimated_completion_date: plan.estimated_completion_date || '',
    success: true,
  }));
};

/** The sample for a goal written exactly like one of them, if there is one. */
export const sampleFor = (goal: string, samples: AIGoalPlan[]): AIGoalPlan | null => {
  const wanted = goal.trim().toLowerCase();
  return samples.find((plan) => plan.title.toLowerCase() === wanted) || null;
};

/** Why a demo call failed, for the screen to say what to do. */
export type DemoFailure = 'busy' | 'running' | 'offline' | 'tooLong' | 'error';

export const demoFailure = (error: unknown): DemoFailure => {
  if (!isAxiosError(error)) return 'error';
  if (!error.response) return 'offline';
  const code = (error.response.data as { code?: string; error?: string } | undefined)?.code
    ?? (error.response.data as { error?: string } | undefined)?.error;
  if (code === 'AI_JOB_LIMIT_REACHED') return 'running';
  if (code === 'AI_MESSAGE_TOO_LONG') return 'tooLong';
  // DEMO_BUSY, the hourly rate limit, a full revision allowance: try again later or use a sample
  if (error.response.status === 429) return 'busy';
  return 'error';
};
